// @flow

import * as React from "react";
import { createPortal } from "react-dom";
import { CSSTransition } from "react-transition-group";
import ally from "ally.js";

import createUniqueID from "../../utils/createUniqueID";
import {
    enableBodyScroll,
    disableBodyScroll,
} from "../../utils/toggleBodyScroll";

// import Button from "../atoms/Button";
import Icon from "../atoms/Icon";

type PropsType = {
    className?: string,
    children?: Array<React.Node> | React.Node,
    active?: boolean,
    aside?: boolean,
    align?: "top" | "bottom",
    asideLeft?: boolean,
    hideClose?: boolean,
    onClose?: Function,
    onExited?: Function,
    onOverlayClick?: Function,
    customTransitionDuration?: any,
};

const ModalContext = React.createContext();

const Modal = (props: PropsType): any => {
    const classList = ["modal"];
    const transitionDuration = props.customTransitionDuration || 1000;
    const [uniqueId] = React.useState(createUniqueID()); // eslint-disable-line
    const modalRef = React.createRef();
    let [previouslyFocusedElement, setPreviouslyFocusedElement] =
        React.useState(null);
    let [allyHandler, setAllyHandler] = React.useState(null);

    React.useEffect((): Function => {
        if (props.active) {
            modalOpened();
        }
        return modalClosed;
    }, []); // eslint-disable-line

    const modalOpened = () => {
        // disable body scrolling
        disableBodyScroll();

        // Save the current focused item
        setPreviouslyFocusedElement(document.activeElement);

        // accessibility - prevent tabbing outside of modal
        requestAnimationFrame(() => {
            if (modalRef.current) {
                setAllyHandler(
                    ally.maintain.disabled({
                        filter: modalRef.current,
                    })
                );
            }
        });
    };

    const modalClosed = () => {
        enableBodyScroll();

        // accessibility - re-enable tabbing outside of modal
        requestAnimationFrame(() => {
            if (allyHandler) {
                allyHandler.disengage();
                if (previouslyFocusedElement) {
                    previouslyFocusedElement.focus();
                }
            }
        });

        if (props.onExited) {
            props.onExited();
        }
    };

    if (props.active) {
        classList.push("is-active");
    }
    if (props.aside) {
        classList.push("modal--aside");
    }
    if (props.align) {
        classList.push(`modal--align-${props.align}`);
    }
    if (props.className) {
        classList.push(props.className);
    }

    let useModalWrapper;
    const children =
        props.children && props.children.length
            ? props.children
            : [props.children];
    children.forEach(child => {
        if (
            child &&
            [
                (<ModalHeader />).type,
                (<ModalContent />).type,
                (<ModalFooter />).type,
            ].includes(child.type)
        ) {
            useModalWrapper = true;
        }
    });

    const ModalWrapper = useModalWrapper ? "div" : React.Fragment;
    const ModalWrapperProps = useModalWrapper
        ? { className: "modal__wrapper" }
        : {};

    return createPortal(
        <CSSTransition
            key={uniqueId}
            in={props.active}
            timeout={transitionDuration}
            classNames={props.aside ? "modal-slide-in" : "fade"}
            onEnter={modalOpened.bind(this)}
            onExited={modalClosed.bind(this)}
            // onExit={modalClosed.bind(this)}
            mountOnEnter
            unmountOnExit
        >
            <div
                ref={modalRef}
                key={uniqueId}
                className={`${classList.join(" ")}`}
            >
                <div
                    className="modal__overlay"
                    onClick={() => {
                        if (props.onOverlayClick) {
                            props.onOverlayClick();
                        }
                    }}
                ></div>

                <div className="modal__inner">
                    <ModalWrapper {...ModalWrapperProps}>
                        <ModalContext.Provider
                            value={{
                                ...props,
                            }}
                        >
                            {props.children}
                        </ModalContext.Provider>
                    </ModalWrapper>
                </div>

                {!useModalWrapper && !props.hideClose ? (
                    <button
                        onClick={() => {
                            if (props.onClose) {
                                props.onClose();
                            }
                        }}
                        className="modal__close"
                        type="button"
                        aria-label="Close modal"
                    >
                        <Icon name="close" />
                    </button>
                ) : null}
            </div>
        </CSSTransition>,
        document.querySelector("body")
    );
};

export default Modal;

class ModalHeader extends React.Component {
    static contextType: any = ModalContext;

    render(): any {
        return (
            <div
                className={`modal__header  ${
                    this.props.clear ? "modal__header--clear" : ""
                }`}
            >
                <div className="modal__header__content">
                    {this.props.children}
                </div>

                {!this.props.hideClose ? (
                    <button
                        onClick={() => {
                            if (this.context.onClose) {
                                this.context.onClose();
                            }
                        }}
                        className="modal__header__close"
                        type="button"
                        aria-label="Close modal"
                    >
                        <Icon name="close" />
                    </button>
                ) : null}
            </div>
        );
    }
}
Modal.Header = ModalHeader;
class ModalContent extends React.Component {
    static contextType: any = ModalContext;

    render(): any {
        return <div className="modal__content">{this.props.children}</div>;
    }
}
Modal.Content = ModalContent;

class ModalFooter extends React.Component {
    static contextType: any = ModalContext;

    render(): any {
        return <div className="modal__footer">{this.props.children}</div>;
    }
}
Modal.Footer = ModalFooter;
